import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import Pagination from '@/components/CommonComponents/Pagination';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey } from '@/helper/utils';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { rolesPermissionsService } from '@/services/rolesPermissionsService/rolesPermissionsService';
import { AppDispatch, RootState } from '@/store';
import {
  deleteRoleById,
  getAllRoles,
  updateRole,
  updateRoleInList,
} from '@/store/slices/rolesPermissionsSlice/rolesPermissionsSlice';
import { Role } from '@/types/adminuser.types';
import { RolesPermissions } from '@/types/rolesPermissions.types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ServerSearchFilter, { ServerFilterField } from '../CommonComponents/ServerSearchFilter';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

const RolesAndPermissions: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { exportFile } = useExportFile();

  const [pageSize, setPageSize] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredRoles, setFilteredRoles] = useState<RolesPermissions[]>([]);
  const [selectedRole, setSelectedRole] = useState<RolesPermissions | null>(null);
  const [filterState, setFilterState] = useState<Record<string, any>>({});

  const { list, loading } = useSelector((state: RootState) => state.rolesPermissions);
  const currentData = list.data;

  const permissions = {
    export: usePermission('export', 'users'),
    delete: usePermission('delete', 'users'),
    edit: usePermission('edit', 'users'),
  };

  const filters = useMemo(
    () => ({
      search: filterState.search || undefined,
      isActive:
        filterState.isActive && filterState.isActive !== 'All Status'
          ? filterState.isActive === 'Active'
          : undefined,
      currentPage: currentPage,
      pageSize: pageSize,
    }),
    [filterState],
  );

  const filterFields: ServerFilterField[] = useMemo(
    () => [
      {
        type: 'search' as const,
        key: 'search',
        placeholder: 'Search Items...',
      },
      {
        type: 'radio-select' as const,
        key: 'isActive',
        label: 'Status',
        options: ['Active', 'Inactive'],
      },
    ],
    [],
  );

  const displayedRoles = useMemo(() => {
    if (!currentData?.length) return [];

    const mapped = currentData.map((role) => ({
      ...role,
      _permissions: permissions,
    }));

    const search = filters?.search?.trim();
    if (!search) return mapped;

    return mapped.filter((item: Role) => {
      const roleName = desanitizeKey(item.name).toLowerCase();
      const searchWords = search.toLowerCase().split(/\s+/);

      // Check if all search words exist in the role name
      return searchWords.every((word: string) => roleName.includes(word));
    });
  }, [currentData, filters.search]);

  useEffect(() => {
    dispatch(getAllRoles(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (!currentData?.length) {
      setFilteredRoles([]);
      return;
    }

    const mappedData = currentData.map((role) => ({
      ...role,
      _permissions: permissions,
    }));

    setFilteredRoles(mappedData);
  }, [currentData]);

  const totalRecords = filteredRoles.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Table columns
  const roleColumns: Column<RolesPermissions>[] = [
    {
      key: 'name',
      header: 'Role Name',
      render: (value, row) => (
        <div className="max-w-92 text-wrap">
          <div className="font-semibold">{desanitizeKey(value)}</div>
          <div className="text-sm line-clamp-1">{row.description}</div>
        </div>
      ),
    },
    {
      key: 'assignedUsers',
      header: 'Assigned Users',
      render: (value) => (
        <span>{value ? (value > 1 ? `${value} users` : `${value} user`) : 'N/A'}</span>
      ),
    },
    { key: 'isActive', header: 'Status' },
  ];

  const handleView = (row: RolesPermissions) => {
    setSelectedRole({
      ...row,
      name: desanitizeKey(row.name),
      createdAt: row.createdAt.split('T')[0],
    });

    setOpenModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleExport = () => {
    exportFile(() => rolesPermissionsService.exportRoles(), `roles_${Date.now()}.csv`);
  };

  const handleStatusToggle = async (id: string, newStatus: boolean) => {
    const roleId = Number(id);
    const selectedRole = currentData.find((role) => role.id === roleId);

    if (!selectedRole) return;

    const oldStatus = selectedRole.isActive ?? false;

    dispatch(
      updateRoleInList({
        id: roleId,
        payload: { isActive: newStatus },
      }),
    );
    try {
      await dispatch(
        updateRole({
          id: roleId,
          payload: { isActive: newStatus },
        }),
      ).unwrap();
    } catch (error) {
      dispatch(
        updateRoleInList({
          id: roleId,
          payload: { isActive: oldStatus },
        }),
      );
      showToast.error(error as string);
    }
  };

  const handleDeleteRow = async (id: number) => {
    try {
      await dispatch(deleteRoleById(id.toString())).unwrap();
    } catch (error) {
      showToast.error(error as string);
    }
  };

  return (
    <PageHeaderWrapper
      title="Roles & Permissions"
      description="Manage system roles and their associated permissions."
      onAdd={() => navigate('/dashboard/system-admin-users')}
      addLabel="Return to Admin List"
      onExport={handleExport}
      exportLabel="Export"
      showActions
      permissions={permissions}
    >
      <div className="flex-shrink-0 mb-4">
        <ServerSearchFilter fields={filterFields} onChange={setFilterState} />
      </div>

      <div>
        {/* Roles Table */}
        <DataTable
          listTitle="Roles & Permissions"
          listHeader={<div className="flex items-center justify-between w-full"></div>}
          columns={roleColumns}
          data={displayedRoles}
          loading={loading}
          onView={handleView}
          onDelete={(row) => handleDeleteRow(row.id)}
          onStatusToggle={handleStatusToggle}
          permissions={permissions}
        />

        {/* Pagination */}
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
        />
      </div>

      <ViewDetailsModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title="Role Details"
        data={selectedRole!}
        fields={[
          { label: 'Role name', value: selectedRole?.name || 'N/A' },
          { label: 'Created On', value: selectedRole?.createdAt || 'N/A' },
          { label: 'Assigned Users', value: selectedRole?.assignedUsers || 'N/A' },
          { label: 'Status', value: selectedRole?.isActive ? 'Active' : 'Inactive' },
          { label: 'Permissions', value: selectedRole?.description || 'N/A' },
        ]}
      />
    </PageHeaderWrapper>
  );
};

export default RolesAndPermissions;
